/**
 * QA HARNESS: EVERY TRACKING TYPE
 *
 * Not a training program. A deliberately exhaustive fixture that exercises
 * every logging surface the app has, so a clickthrough can verify all of them
 * in one session instead of hunting for a real program that happens to use a
 * rare shape.
 *
 * Coverage, one per block so each is reachable in a single day:
 *
 *   Day 1 — STRENGTH SHAPES
 *     weight-reps (barbell), weight-reps (dumbbell, per-hand), bodyweight-reps,
 *     assisted (inverted load direction), SUPERSET pair, timed hold
 *
 *   Day 2 — CONDITIONING SHAPES
 *     CIRCUIT (rounds), AMRAP, EMOM, interval, cardio distance-time
 *
 *   Day 3 — LOADED CARRY & FIELD SHAPES
 *     carry (metres), sled push, ruck (load + distance + time),
 *     swim (manual/retroactive — no phone at the pool), timed hold under load
 *
 * WHY THIS EXISTS: the tracking types diverge sharply in how they log
 * (`sets` vs `circuitSets` vs bare `actualTime`), and several defects found in
 * the 2026-08 audits were invisible precisely because no single session
 * touched them all — carries storing metres in `reps`, circuits writing to a
 * schema no engine read, timed holds recording no duration at all, swim
 * durations parsing to zero.
 *
 * SAFE TO DELETE once real coverage exists. It is registered like any other
 * program so it can be selected in a demo account; it is NOT surfaced as a
 * recommendation (see `isActive: false`).
 */

import type {
  WorkoutProgram,
  WorkoutTemplateDay,
  ProgramMetadata,
  ProgramPeriodizationModel,
} from '../types/program';

const metadata: ProgramMetadata = {
  id: 'qa-all-tracking-types',
  name: 'QA — All Tracking Types',
  description:
    'Test fixture. One week that exercises every logging surface: strength, dumbbell per-hand, bodyweight, assisted, supersets, timed holds, circuits, AMRAP, EMOM, intervals, cardio, carries, sled, ruck and swim. Use for clickthrough verification, not for training.',
  level: 'operator',
  experienceLevel: 'intermediate',
  duration: 1,
  daysPerWeek: 3,
  goals: ['strength', 'conditioning'],
  difficulty: 'intermediate',
  tags: ['qa', 'test-fixture', 'internal'],
  emphasis: 'Coverage of every tracking type in as few sessions as possible',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  // Kept out of the recommender/browse surfaces — selectable directly, but
  // never suggested to a real user.
  isActive: false,
  usesPeriodization: false,
  hasRangeDay: false,
  requiredEquipment: 'full-gym',
};

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    {
      weeks: [1],
      type: 'accumulation',
      name: 'QA Week',
      focus: 'Exercise every tracking type',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
  ],
};

// ============================================================================
// DAY 1 — STRENGTH SHAPES
// ============================================================================

const day1: WorkoutTemplateDay = {
  name: 'QA 1 — Strength Shapes',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Prep',
      type: 'warmup',
      exercises: [
        { name: 'Leg Swings', sets: 1, reps: 10, format: 'bodyweight', details: 'Each leg' },
        { name: 'Band Pull-Apart', sets: 2, reps: 15, format: 'bodyweight' },
      ],
    },
    {
      // weight-reps, barbell. The bread-and-butter path.
      name: 'Main Strength',
      type: 'strength',
      exercises: [
        { name: 'Back Squat', sets: 4, reps: 5, intensity: 'progressive', rest: '2-3 min', rpe: 'RIR 2', details: 'Primary loaded lift' },
      ],
    },
    {
      // SUPERSET — two exercises alternating. Renders as a paired block.
      name: 'Superset (A1/A2)',
      type: 'strength',
      format: 'superset',
      exercises: [
        { name: 'Bench Press', sets: 3, reps: 8, intensity: 'progressive', rest: '90s', rpe: 'RIR 2' },
        { name: 'Barbell Row', sets: 3, reps: 8, intensity: 'progressive', rest: '90s', rpe: 'RIR 2' },
      ],
    },
    {
      // Per-hand dumbbell load (x2 volume correction) + bodyweight + assisted.
      name: 'Accessories',
      type: 'strength',
      exercises: [
        { name: 'Dumbbell Press', sets: 3, reps: 10, intensity: 'progressive', rest: '60s', details: 'Per-hand load' },
        { name: 'Push-Up', sets: 3, reps: 15, format: 'bodyweight', rest: '60s' },
        { name: 'Assisted Pull-Up', sets: 3, reps: 8, rest: '90s', details: 'Assistance load — MORE is easier' },
      ],
    },
    {
      // trackingType 'time' — the hold path.
      name: 'Core',
      type: 'core',
      exercises: [
        { name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight', rest: '45s', details: 'Timed hold' },
        { name: 'Side Plank', sets: 2, reps: '30s', format: 'bodyweight', rest: '30s', details: 'Each side' },
      ],
    },
  ],
};

// ============================================================================
// DAY 2 — CONDITIONING SHAPES
// ============================================================================

const day2: WorkoutTemplateDay = {
  name: 'QA 2 — Conditioning Shapes',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'warmup',
      exercises: [
        { name: 'Jumping Jacks', sets: 2, reps: 20, format: 'bodyweight' },
      ],
    },
    {
      // CIRCUIT — writes circuitSets[], not sets[].
      name: 'Circuit',
      type: 'conditioning',
      format: 'circuit',
      exercises: [
        { name: 'Kettlebell Swing', sets: 4, reps: 15, format: 'circuit', rest: '60s' },
        { name: 'Goblet Squat', sets: 4, reps: 12, format: 'circuit', rest: '60s' },
        { name: 'Push-Up', sets: 4, reps: 15, format: 'circuit', rest: '60s' },
      ],
    },
    {
      // AMRAP — rounds in a time cap.
      name: 'AMRAP',
      type: 'conditioning',
      format: 'amrap',
      exercises: [
        { name: 'Burpee', sets: 1, reps: 10, format: 'amrap', details: '10 min AMRAP' },
        { name: 'Air Squat', sets: 1, reps: 15, format: 'amrap' },
      ],
    },
    {
      // EMOM — every minute on the minute.
      name: 'EMOM',
      type: 'conditioning',
      format: 'emom',
      exercises: [
        { name: 'Box Jump', sets: 8, reps: 5, format: 'emom', details: '8 min EMOM' },
      ],
    },
    {
      // INTERVAL — the shape with no V2 logging surface (see audit).
      name: 'Intervals',
      type: 'conditioning',
      format: 'intervals',
      exercises: [
        { name: 'Assault Bike', sets: 6, reps: 1, format: 'intervals', rest: '60s', details: '6 × 30s hard / 60s easy' },
      ],
    },
    {
      // distance-time cardio.
      name: 'Cardio',
      type: 'cardio',
      exercises: [
        { name: 'Run', sets: 1, distance: '2 miles', format: 'cardio', rpe: 5, details: 'Z2, conversational' },
      ],
    },
  ],
};

// ============================================================================
// DAY 3 — LOADED CARRY & FIELD SHAPES
// ============================================================================

const day3: WorkoutTemplateDay = {
  name: 'QA 3 — Carry & Field Shapes',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Prep',
      type: 'warmup',
      exercises: [
        { name: 'Dead Bug', sets: 2, reps: 10, format: 'bodyweight' },
      ],
    },
    {
      // CARRY — metres live in the reps slot.
      name: 'Carries',
      type: 'carry',
      exercises: [
        { name: "Farmer's Walk", sets: 4, reps: '30-50m', intensity: 'progressive', rest: '60s', details: 'Distance is the progression lever' },
        { name: 'Suitcase Carry', sets: 3, reps: '40m/side', intensity: 'progressive', rest: '45s' },
      ],
    },
    {
      name: 'Sled',
      type: 'conditioning',
      exercises: [
        { name: 'Sled Push', sets: 5, reps: '20m', intensity: 'progressive', rest: '90s' },
      ],
    },
    {
      // RUCK — load + distance + time.
      name: 'Ruck',
      type: 'ruck',
      exercises: [
        { name: 'Ruck March', sets: 1, distance: '2 miles', format: 'ruck', rpe: 6, details: '35 lbs. Z2 pace.' },
      ],
    },
    {
      // SWIM — manual/retroactive entry. You cannot take a phone into the
      // pool, so this must be loggable after the fact from a total time and
      // distance. Per-interval splits are offered, never required.
      name: 'Swim',
      type: 'conditioning',
      exercises: [
        { name: 'Swim', sets: 1, distance: '500m', format: 'cardio', rpe: 6, details: 'Log total time + distance afterwards — no phone poolside.' },
      ],
    },
    {
      // Loaded hold — timed AND carrying external load.
      name: 'Finisher',
      type: 'core',
      exercises: [
        { name: 'Plank', sets: 2, reps: '60s', format: 'bodyweight', rest: '45s', details: 'Add load if 60s is easy' },
      ],
    },
  ],
};

export const allTrackingTypesQA: WorkoutProgram = {
  metadata,
  periodization,
  workouts: [],
  templates: {
    'training-day1': day1,
    'training-day2': day2,
    'training-day3': day3,
    'week1-day1': day1,
    'week1-day2': day2,
    'week1-day3': day3,
  },
};
